import { IconCaretDownFilled, IconCaretUpFilled, IconUsers } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState, useMemo } from 'react';
import {
    Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from 'reactstrap';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { Link } from 'react-router-dom';

import classNames from 'classnames';
import { IOrganization } from 'hooks/useGetUserOrganizations';
import { useAuthContext } from 'contexts/AuthContext';
import { URLS } from 'services/urls';
import { NSSearchBar } from 'bricks';
import { useDebounce } from 'hooks/useDebounce';
import { DEBOUNCE_DELAY_MS } from '../../constants';
import './OrganizationMenu.scss';

export interface IOrganizationMenuProps {
    organizations: IOrganization[];
}

const OrganizationMenu = ({ organizations }: IOrganizationMenuProps) => {
    const { selectedOrganizationId, handleUpdateSelectedOrganization } = useAuthContext();
    const [isExpanded, setIsExpanded] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const debouncedSearchValue = useDebounce(searchValue, DEBOUNCE_DELAY_MS);

    const toggleIsExpanded = () => setIsExpanded(!isExpanded);

    const filterOrganizations = (orgs: IOrganization[], query: string): IOrganization[] => {
        if (!query) return orgs;
        return orgs.filter(org => org.name.toLowerCase().includes(query.toLowerCase()));
    };

    const filteredAndSortedOrgs = useMemo(() => {
        const filtered = filterOrganizations(organizations, debouncedSearchValue);
        return [...filtered].sort((a, b) => {
            if (a.correlationId === selectedOrganizationId) {
                return -1;
            }
            if (b.correlationId === selectedOrganizationId) {
                return 1;
            }
            return a.name.localeCompare(b.name);
        });
    }, [organizations, debouncedSearchValue, selectedOrganizationId]);

    const selectedOrg = useMemo(
        () => organizations.find(org => org.correlationId === selectedOrganizationId),
        [organizations, selectedOrganizationId],
    );

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    };

    return (
        <Dropdown isOpen={isExpanded} toggle={toggleIsExpanded} direction="down">
            <DropdownToggle tag="span">
                <div
                    className={classNames(
                        'OrganizationMenu__toggle d-flex align-items-center justify-content-between cursor--pointer text-white border p-1',
                        { 'border-primary': isExpanded },
                    )}
                    onClick={toggleIsExpanded}
                    onKeyDown={toggleIsExpanded}
                    role="button"
                    tabIndex={0}
                    data-testid="org-menu-toggle"
                >   
                    <div
                        className="text-truncate d-flex align-items-center pl-1"
                    >
                        <NSIcon className="icon" icon={IconUsers} size={14} />
                        <div className="ml-1" data-testid={`selected-org-${selectedOrg?.name}`}>
                            {selectedOrg?.name || ''}
                        </div>
                    </div>
                    <NSIcon className="icon p-1" icon={isExpanded ? IconCaretUpFilled : IconCaretDownFilled} data-testid="caret-icon" />
                </div>

                <DropdownMenu className="mt-1 p-0" style={{ width: '300px' }}>
                    <div
                        className="p-2 border-bottom"
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => {
                            if (e.key === 'Escape' || e.key === 'Enter') {
                                return;
                            }
                            e.stopPropagation();
                        }}
                        role="presentation"
                    >
                        <NSSearchBar
                            searchValue={searchValue}
                            handleChangeSearchValue={handleSearchChange}
                            placeholder="Search organizations..."
                            containerClassName="mb-0"
                        />
                    </div>
                    <OverlayScrollbarsComponent
                        options={{
                            scrollbars: { autoHide: 'leave' },
                            overflow: { y: 'scroll' },
                        }}
                        className="pr-2 overflow-auto"
                        style={{ maxHeight: '600px' }}
                        defer
                    >
                        {filteredAndSortedOrgs.map(org => (
                            <DropdownItem
                                tag={Link}
                                to={URLS.DEALS.HOME}
                                onClick={() => handleUpdateSelectedOrganization(org.id)}
                                title={org.name}
                                key={org.correlationId}
                                className="OrganizationMenu__item ml-1"
                                active={org.correlationId === selectedOrganizationId}
                            >
                                <div className="text-truncate">{org.name}</div>
                            </DropdownItem>
                        ))}
                    </OverlayScrollbarsComponent>
                </DropdownMenu>
            </DropdownToggle>
        </Dropdown>
    );
};

export default OrganizationMenu;
